import React, { Fragment, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { useNavigate } from '@tanstack/react-router';
import { DeleteFinancialRecordModal } from './modals';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';

export interface ResidentPaymentHistory {
  id: string;
  residentName: string;
  houseNo: string;
  programFees: number;
  customCharges: number;
  outstandingFee: number;
  totalDue: number;
  paidAmount: number;
  lastPayment: string;
  status: 'PAID' | 'PARTIAL' | 'UNPAID';
}

interface FinancialPaymentHistoryTableProps {
  data: ResidentPaymentHistory[];
  columns?: ColumnDef<ResidentPaymentHistory, any>[];
  isLoading?: boolean;
  className?: string;
}

export const FinancialPaymentHistoryTable: React.FC<
  FinancialPaymentHistoryTableProps
> = ({ data, columns = paymentColumns, isLoading, className = '' }) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading)
    return (
      <div className="p-4 space-y-2">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-300 animate-pulse rounded" />
        ))}
      </div>
    );

  return (
    <div
      className={`min-w-3xl ${className}`}
      style={{ scrollbarWidth: 'none' }}
    >
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="p-2 md:p-4 text-sm md:text-base text-center border">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-2 md:p-4 text-sm md:text-base text-start border">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="py-6 text-start text-gray-400"
              >
                <div className="text-center">
                  No records found.
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ✅ Columns setup
const paymentColumns: ColumnDef<ResidentPaymentHistory>[] = [
  {
    accessorKey: 'residentName',
    header: () => (
      <div className="text-center font-semibold">Resident Name</div>
    ),
    cell: (info) => (
      <div className="py-3">{String(info.getValue() ?? '-')}</div>
    ),
  },
  {
    accessorKey: 'houseNo',
    header: () => (
      <div className="text-center font-semibold">House No.</div>
    ),
    cell: (info) => (
      <div className="py-3 text-center">{String(info.getValue() ?? '-')}</div>
    ),
  },
  {
    accessorKey: 'programFees',
    header: () => (
      <div className="text-center font-semibold">Program Fees</div>
    ),
    cell: (info) => (
      <div className="py-3 text-center">${String(info.getValue() ?? 0)}</div>
    ),
  },
  {
    accessorKey: 'customCharges',
    header: () => (
      <div className="text-center font-semibold">Custom Charges</div>
    ),
    cell: (info) => (
      <div className="py-3 text-center">${String(info.getValue() ?? 0)}</div>
    ),
  },
  {
    accessorKey: 'outstandingFee',
    header: () => (
      <div className="text-center font-semibold">Outstanding Fee</div>
    ),
    cell: (info) => (
      <div className="py-3 text-center text-red-600">
        ${String(info.getValue() ?? 0)}
      </div>
    ),
  },
  {
    accessorKey: 'totalDue',
    header: () => (
      <div className="text-center font-semibold">Total Due</div>
    ),
    cell: (info) => (
      <div className="py-3 text-center font-medium">
        ${String(info.getValue() ?? 0)}
      </div>
    ),
  },
  {
    accessorKey: 'paidAmount',
    header: () => (
      <div className="text-center font-semibold">Paid Amount</div>
    ),
    cell: (info) => (
      <div className="py-3 text-center text-green-700">
        ${String(info.getValue() ?? 0)}
      </div>
    ),
  },
  {
    accessorKey: 'lastPayment',
    header: () => (
      <div className="text-center font-semibold">Last Payment</div>
    ),
    cell: (info) => (
      <div className="py-3 text-center">
        {new Date(String(info.getValue()) as string).toLocaleDateString()}
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: () => <div className="text-center font-semibold">Status</div>,
    cell: (info) => {
      const status = String(
        info.getValue(),
      ) as ResidentPaymentHistory['status'];
      const color =
        status === 'PAID'
          ? 'bg-green-100 text-green-700'
          : status === 'PARTIAL'
          ? 'bg-yellow-100 text-yellow-700'
          : 'bg-red-100 text-red-700';

      return (
        <div className="px-6 py-3 text-center">
          <span className={`px-2 py-1 rounded text-xs font-semibold ${color}`}>
            {status}
          </span>
        </div>
      );
    },
  },
  {
    id: 'actions',
    header: () => <div className="font-semibold text-center"></div>,
    cell: ({ row }) => <ActionsCell historyRecord={row.original} />,
  },
];

// ✅ ActionsCell component to safely use hooks
const ActionsCell: React.FC<{ historyRecord: any }> = ({ historyRecord }) => {
  const navigate = useNavigate();

  /** -----------------------------
   *   Floating UI positioning
   *  ----------------------------- */
  const { refs, floatingStyles } = useFloating({
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-end',
  });

  const handleView = () =>
    navigate({ to: `/financials/history/resident/${historyRecord.id}` });
  const handleEdit = () =>
    navigate({ to: `/houses/resident/${historyRecord.id}` });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  useState(false);

  return (
    <div className="flex justify-center">
      <Menu as="div" className="relative inline-block text-left">
        <MenuButton
          className="text-gray-500 hover:text-gray-700 cursor-pointer w-5"
          ref={refs.setReference}
        >
          <svg
            width="4"
            className="mx-auto"
            height="16"
            viewBox="0 0 4 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 4C0.89543 4 -4.82823e-08 3.10457 0 2C4.82823e-08 0.89543 0.895431 -4.82823e-08 2 0C3.10457 4.82823e-08 4 0.895431 4 2C4 3.10457 3.10457 4 2 4Z"
              fill="#111827"
            />
            <path
              d="M2 10C0.89543 10 -4.82823e-08 9.10457 0 8C4.82823e-08 6.89543 0.895431 6 2 6C3.10457 6 4 6.89543 4 8C4 9.10457 3.10457 10 2 10Z"
              fill="#111827"
            />
            <path
              d="M2 16C0.89543 16 -4.82823e-08 15.1046 0 14C4.82823e-08 12.8954 0.895431 12 2 12C3.10457 12 4 12.8954 4 14C4 15.1046 3.10457 16 2 16Z"
              fill="#111827"
            />
          </svg>
        </MenuButton>

        <Transition
          as={Fragment}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-in"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <MenuItems
            ref={refs.setFloating}
            style={floatingStyles}
            className="absolute right-0 mt-2 w-50 origin-top-right bg-white border border-gray-200 shadow-lg focus:outline-none z-50"
          >
            <div className="py-1">
              <MenuItem>
                {() => (
                  <button
                    onClick={handleView}
                    className={`w-full text-left p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200 `}
                  >
                    <svg
                      width="22"
                      height="15"
                      viewBox="0 0 22 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 12C12.25 12 13.3125 11.5625 14.1875 10.6875C15.0625 9.8125 15.5 8.75 15.5 7.5C15.5 6.25 15.0625 5.1875 14.1875 4.3125C13.3125 3.4375 12.25 3 11 3C9.75 3 8.6875 3.4375 7.8125 4.3125C6.9375 5.1875 6.5 6.25 6.5 7.5C6.5 8.75 6.9375 9.8125 7.8125 10.6875C8.6875 11.5625 9.75 12 11 12ZM11 10.2C10.25 10.2 9.6125 9.9375 9.0875 9.4125C8.5625 8.8875 8.3 8.25 8.3 7.5C8.3 6.75 8.5625 6.1125 9.0875 5.5875C9.6125 5.0625 10.25 4.8 11 4.8C11.75 4.8 12.3875 5.0625 12.9125 5.5875C13.4375 6.1125 13.7 6.75 13.7 7.5C13.7 8.25 13.4375 8.8875 12.9125 9.4125C12.3875 9.9375 11.75 10.2 11 10.2ZM11 15C8.56667 15 6.35 14.3208 4.35 12.9625C2.35 11.6042 0.9 9.78333 0 7.5C0.9 5.21667 2.35 3.39583 4.35 2.0375C6.35 0.679167 8.56667 0 11 0C13.4333 0 15.65 0.679167 17.65 2.0375C19.65 3.39583 21.1 5.21667 22 7.5C21.1 9.78333 19.65 11.6042 17.65 12.9625C15.65 14.3208 13.4333 15 11 15ZM11 13C12.8833 13 14.6125 12.5042 16.1875 11.5125C17.7625 10.5208 18.9667 9.18333 19.8 7.5C18.9667 5.81667 17.7625 4.47917 16.1875 3.4875C14.6125 2.49583 12.8833 2 11 2C9.11667 2 7.3875 2.49583 5.8125 3.4875C4.2375 4.47917 3.03333 5.81667 2.2 7.5C3.03333 9.18333 4.2375 10.5208 5.8125 11.5125C7.3875 12.5042 9.11667 13 11 13Z"
                        fill="#474747"
                      />
                    </svg>
                    <span>View Details</span>
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {() => (
                  <button
                    onClick={handleEdit}
                    className={`w-full text-left p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200`}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 16H3.425L13.2 6.225L11.775 4.8L2 14.575V16ZM0 18V13.75L13.2 0.575C13.4 0.391667 13.6208 0.25 13.8625 0.15C14.1042 0.05 14.3583 0 14.625 0C14.8917 0 15.15 0.05 15.4 0.15C15.65 0.25 15.8667 0.4 16.05 0.6L17.425 2C17.625 2.18333 17.7708 2.4 17.8625 2.65C17.9542 2.9 18 3.15 18 3.4C18 3.66667 17.9542 3.92083 17.8625 4.1625C17.7708 4.40417 17.625 4.625 17.425 4.825L4.25 18H0ZM12.475 5.525L11.775 4.8L13.2 6.225L12.475 5.525Z"
                        fill="#474747"
                      />
                    </svg>

                    <span>View Resident</span>
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {() => (
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    className={`w-full text-left p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200 `}
                  >
                    <svg
                      width="16"
                      height="18"
                      viewBox="0 0 16 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 18C2.45 18 1.97917 17.8042 1.5875 17.4125C1.19583 17.0208 1 16.55 1 16V3H0V1H5V0H11V1H16V3H15V16C15 16.55 14.8042 17.0208 14.4125 17.4125C14.0208 17.8042 13.55 18 13 18H3ZM13 3H3V16H13V3ZM5 14H7V5H5V14ZM9 14H11V5H9V14Z"
                        fill="#E03A3D"
                      />
                    </svg>

                    <span>Delete Record</span>
                  </button>
                )}
              </MenuItem>
            </div>
          </MenuItems>
        </Transition>
      </Menu>

      {/* Delete Modal */}
      <DeleteFinancialRecordModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={(id) => {
          // Handle delete confirmation
          console.log('Delete record:', id);
          setIsDeleteModalOpen(false);
        }}
        record={historyRecord}
      />
    </div>
  );
};
